// HeaderLinks.jsx (updated to use NotificationList)

import {
	Box,
	Flex,
	Icon,
	Link,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Text,
	useColorModeValue,
	useToast,
} from '@chakra-ui/react';
import { notificationService } from 'api/services/notificationService';
import NotificationCount from 'components/navbar/components/NotificationCount';
import NotificationList from 'components/navbar/components/NotificationList';
import Configurator from 'components/navbar/Configurator';
import { SidebarResponsive } from 'components/sidebar/Sidebar';
import { ALERT } from 'constant/messages';
import { NAVIGATE } from 'constant/navigation';
import { useEffect, useState } from 'react';
import { FaRegUser } from 'react-icons/fa6';
import { MdNotificationsNone } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import routes from 'routes';
import { getSocket } from 'socket/socket';
import { userActions } from 'store/user';
import { fetchData } from 'utils/hooks';
import {
	fetchConversionRates,
	formatBalance,
	handleCurrencyChange,
} from 'utils/rates';

export default function HeaderLinks(props) {
	const { secondary, theme, setTheme } = props;
	const toast = useToast();
	const TOKEN = useSelector((state) => state.auth.tokenId);
	const {
		userFirstName,
		userBalanceTotal,
		userNotificationsCount,
		userNotifications,
	} = useSelector((state) => state.user);

	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [mxnValue, setMxnValue] = useState(0);
	const [usdValue, setUsdValue] = useState(0);
	const [currentCurrency, setCurrentCurrency] = useState('SATS');
	const [message, setMessage] = useState({
		state: false,
		status: '',
		description: '',
	});

	useEffect(() => {
		fetchNotifications();
		fetchConversionRates(setMxnValue, setUsdValue);
		const socket = getSocket();
		if (!socket) {
			console.warn('[Socket] Not initialized — skipping listener setup.');
			return;
		}

		const handleSocketNotification = (notification) => {
			console.log('[Socket] Notification received.');

			dispatch((dispatch, getState) => {
				const { userNotifications } = getState().user;

				const updated = [...userNotifications, notification];
				const count = updated.filter((n) => !n.viewed).length;

				dispatch(
					userActions.setUserNotifications({
						userNotifications: updated,
						userNotificationsCount: count,
					})
				);
			});

			toast({
				id: notification.id,
				title: notification.title || 'Nueva notificación',
				description: notification.description,
				duration: 6000,
				isClosable: false,
				position: 'top-center',
				status:
					notification.event === 'withdrawal.success'
						? 'success'
						: notification.event === 'withdrawal.failure'
						? 'error'
						: 'info',
			});
		};

		const handleSocketBalance = (balance) => {
			console.log('[Socket] Balance update received.');

			const updatePayload = {};
			if (Number.isFinite(balance.balanceAvailable)) {
				updatePayload.userBalanceAvailable =
					balance.balanceAvailable.toFixed(0);
			}
			if (Number.isFinite(balance.balancePending)) {
				updatePayload.userBalancePending = balance.balancePending.toFixed(0);
			}
			if (Number.isFinite(balance.balanceEarned)) {
				updatePayload.userBalanceEarned = balance.balanceEarned.toFixed(0);
			}
			if (Number.isFinite(balance.balanceTotal)) {
				updatePayload.userBalanceTotal = balance.balanceTotal.toFixed(0);
			}
			if (Object.keys(updatePayload).length > 0) {
				dispatch(userActions.setUserBalance(updatePayload));
			}
		};

		const setupListeners = () => {
			socket.on('event:notification', handleSocketNotification);
			socket.on('event:userBalanceUpdate', handleSocketBalance);
			console.log('[Socket] Event listeners registered');
		};

		if (socket.connected) {
			setupListeners();
		} else {
			socket.once('connect', setupListeners);
		}

		return () => {
			socket.off('event:notification', handleSocketNotification);
			socket.off('event:userBalanceUpdate', handleSocketBalance);
			socket.off('connect', setupListeners);
		};
	}, [dispatch]);

	const fetchNotifications = async () => {
		fetchData({
			apiCalls: [
				() => notificationService.getAll(TOKEN),
				() => notificationService.countPending(TOKEN),
			],
			setStores: [
				(data) =>
					dispatch(
						userActions.setUserNotifications({ userNotifications: data })
					),
				(data) =>
					dispatch(
						userActions.setUserNotifications({
							userNotificationsCount: data || 0,
						})
					),
			],
			alertMessages: ALERT,
			setMessage,
			setLoading,
		});
	};

	const handleSetAllAsViewed = () => {
		if (userNotifications.every((n) => n.viewed)) return;
		notificationService.setAllAsViewed(TOKEN);
		const updated = userNotifications.map((n) => ({ ...n, viewed: true }));
		dispatch(
			userActions.setUserNotifications({
				userNotifications: updated,
				userNotificationsCount: 0,
			})
		);
	};

	const handleSetAsViewed = (ID) => {
		const notification = userNotifications.find((n) => n.id === ID);
		if (!notification || notification.viewed) return;
		notificationService.setAsViewed(TOKEN, ID);
		const updated = userNotifications.map((n) =>
			n.id === ID ? { ...n, viewed: true } : n
		);
		const count = updated.filter((n) => !n.viewed).length;
		dispatch(
			userActions.setUserNotifications({
				userNotifications: updated,
				userNotificationsCount: count,
			})
		);
	};

	const menuBg = useColorModeValue('gray.100', 'gray.900');
	const textColor = useColorModeValue('gray.900', 'gray.100');
	const navbarIcon = useColorModeValue('gray.900', 'gray.100');
	const hoverTextColor = useColorModeValue('gray.900', 'gray.900');
	const textBalanceColor = useColorModeValue('gray.100', 'gray.900');
	const textColorBrand = useColorModeValue('brand.700', 'brand.400');
	const btcColor = useColorModeValue('brand.700', 'gray.100');
	const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
	const shadow = useColorModeValue(
		'14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
		'14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
	);

	return (
		<Flex
			w={{ sm: '100%', md: 'auto' }}
			alignItems='center'
			flexDirection='row'
			bg={menuBg}
			flexWrap={secondary ? { base: 'wrap', md: 'nowrap' } : 'unset'}
			p='10px'
			borderRadius='999px'
			boxShadow={shadow}
			justifyContent={{ sm: 'space-around' }}
		>
			<Flex
				bg={btcColor}
				display={{ base: 'none', md: 'flex' }}
				borderRadius='20px'
				ms='auto'
				p='6px'
				align='center'
				me='6px'
				ml={{ sm: 0 }}
				cursor={'pointer'}
				onClick={() => handleCurrencyChange(setCurrentCurrency)}
			>
				<Text
					pl={2}
					color={textBalanceColor}
					fontSize='sm'
					fontWeight='500'
					me='6px'
				>
					Balance total:
				</Text>
				<Text
					pr={1}
					color={textBalanceColor}
					fontSize='sm'
					fontWeight='700'
					me='6px'
				>
					{formatBalance(userBalanceTotal, currentCurrency, usdValue, mxnValue)}
				</Text>
			</Flex>

			<SidebarResponsive routes={routes} />
			<Menu closeOnSelect={false}>
				<MenuButton p='0px' mr={2} onClick={fetchNotifications}>
					<Icon
						mt='6px'
						as={MdNotificationsNone}
						color={navbarIcon}
						w='18px'
						h='18px'
						me='10px'
					/>
					{userNotificationsCount > 0 && (
						<Box
							position='absolute'
							top={{ base: '40px', md: '10px' }}
							ml={1}
							bg='blue.500'
							color='gray.100'
							borderRadius='50%'
							px={2.5}
							py={0.8}
							fontSize='0.6rem'
							fontWeight='bold'
							display='flex'
							alignItems='center'
							justifyContent='center'
						>
							{userNotificationsCount > 99 ? '99+' : userNotificationsCount}
						</Box>
					)}
				</MenuButton>
				<MenuList
					boxShadow={shadow}
					p='20px'
					borderRadius='20px'
					bg={menuBg}
					border='none'
					mt='22px'
					me={{ base: '30px', md: 'unset' }}
					minW={{ base: 'unset', md: '400px', xl: '450px' }}
					maxW={{ base: '360px', md: 'unset' }}
					maxH={{ base: '70vh' }}
					overflowY={{ base: 'scroll' }}
				>
					<Flex w='100%' mb='20px'>
						<NotificationCount
							textColor={textColor}
							notificationsCount={userNotificationsCount}
						/>
						{userNotificationsCount > 0 && (
							<Text
								fontSize='sm'
								fontWeight='500'
								color={textColorBrand}
								ms='auto'
								cursor='pointer'
								onClick={handleSetAllAsViewed}
							>
								Marcar todas como leídas
							</Text>
						)}
					</Flex>
					<NotificationList
						loading={loading}
						notifications={userNotifications}
						onSetAsViewed={handleSetAsViewed}
					/>
				</MenuList>
			</Menu>

			<Configurator
				mini={props.mini}
				setMini={props.setMini}
				theme={theme}
				setTheme={setTheme}
			/>
			<Menu>
				<MenuButton p='0px'>
					<Icon
						mt='6px'
						as={FaRegUser}
						color={navbarIcon}
						w='15px'
						h='15px'
						me='10px'
					/>
				</MenuButton>
				<MenuList
					boxShadow={shadow}
					p='0px'
					mt='10px'
					borderRadius='20px'
					bg={menuBg}
					border='none'
				>
					<Flex w='100%' mb='0px'>
						<Text
							ps='20px'
							pt='16px'
							pb='10px'
							w='100%'
							borderBottom='1px solid'
							borderColor={borderColor}
							fontSize='sm'
							fontWeight='700'
							color={textColor}
						>
							👋&nbsp; Hola, {userFirstName}
						</Text>
					</Flex>
					<Flex flexDirection='column' p='10px'>
						<NavLink to={NAVIGATE.PROFILE_OVERVIEW}>
							<MenuItem
								bg='inherit'
								_hover={{ bg: btcColor, color: hoverTextColor }}
								borderRadius='8px'
								px='14px'
							>
								<Text fontSize='sm'>Perfil</Text>
							</MenuItem>
						</NavLink>
						<MenuItem
							bg='inherit'
							_hover={{ bg: btcColor, color: hoverTextColor }}
							color='red.400'
							borderRadius='8px'
							px='14px'
						>
							<Link href='/auth'>
								<Text fontSize='sm'>Cerrar sesión</Text>
							</Link>
						</MenuItem>
					</Flex>
				</MenuList>
			</Menu>
		</Flex>
	);
}
